import React, { useState, FC } from 'react';
import Head from 'next/head';
import cn from 'clsx';

import LazyToggleContent from '../LazyToggleContent';

import useWithinSizeList from '../../hooks/useWithinSizeList';
import useIntersection from '../../hooks/useIntersection';

import { SIZE_SM, SIZE_MD } from '../../utils/screens';

const USE_MOBILE_SIZING_LIST = [SIZE_SM, SIZE_MD];

interface Props {
  className?: string;
  imageClassName?: string;
  image: any;
  mobileImage?: any;
  aspectRatio?: any;
  mobileAspectRatio?: any;
  loadedClassName?: string;
  loadingClassName?: string;
  mobileSizeList?: string[];
  preload?: boolean;
  priority?: boolean;
  alt?: string;
}

const LazyImage: FC<Props> = ({
  className,
  imageClassName,
  loadingClassName,
  loadedClassName,
  image,
  mobileImage,
  aspectRatio,
  mobileAspectRatio,
  mobileSizeList = USE_MOBILE_SIZING_LIST,
  priority = false,
  preload = false,
  alt = '',
  ...props
}) => {
  const isMobile = useWithinSizeList(mobileSizeList);
  const [isImageLoaded, setIsLoaded] = useState(priority);
  const [ref, inView] = useIntersection({
    triggerOnce: true,
    rootMargin: '100px 0px',
    initialInView: priority,
    skip: priority,
  });

  const imageToUse = isMobile && mobileImage ? mobileImage : image;
  let aspectRatioToUse;
  if (imageToUse === mobileImage && mobileAspectRatio) {
    aspectRatioToUse = mobileAspectRatio;
  } else if (imageToUse === image && aspectRatio) {
    aspectRatioToUse = aspectRatio;
  }

  return (
    <div
      className={cn('relative', className, isImageLoaded ? loadedClassName : loadingClassName)}
      ref={ref}
    >
      {preload && (
        <Head>
          <link as="image" href={imageToUse} key={`__nimg-${image}`} rel="preload"></link>
        </Head>
      )}
      <div
        className={aspectRatioToUse ? 'relative h-0' : undefined}
        style={aspectRatioToUse ? { marginBottom: aspectRatioToUse } : undefined}
      >
        <LazyToggleContent show={isImageLoaded || priority || inView}>
          <img
            alt={alt}
            className={cn(
              imageClassName,
              aspectRatioToUse ? 'absolute inset-0 object-contain' : undefined
            )}
            // @ts-expect-error
            importance={priority ? 'high' : 'auto'}
            onLoad={() => setIsLoaded(true)}
            src={imageToUse}
            {...props}
          />
        </LazyToggleContent>
      </div>
    </div>
  );
};

export default LazyImage;
